import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Settings,
  AlertCircle,
  Loader,
  RefreshCw,
  Upload
} from 'lucide-react'
import Button from '../ui/Button'
import Card from '../ui/Card'
import ProjectCard from './ProjectCard'
import AddProjectModal from './AddProjectModal'
import ProjectEditor from './ProjectEditor'
import { useTMSProjects } from '../../hooks/useTMSProjects'

const TMSProjectManager = () => {
  const { t } = useTranslation()

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)

  // Use the TMS projects hook
  const {
    projects,
    defaultProjectId,
    isLoading,
    error,
    refetch,
    deleteProject,
    setDefaultProject,
    testConnection,
    isDeletingProject
  } = useTMSProjects()

  const handleEditProject = (project) => {
    setEditingProject(project)
  }

  const handleDeleteProject = (project) => {
    setShowDeleteConfirm(project)
  }

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      deleteProject(showDeleteConfirm.project_id)
      setShowDeleteConfirm(null)
    }
  }

  const handleSetDefault = (project) => {
    setDefaultProject(project.project_id)
  }

  const handleTestConnection = (project) => {
    testConnection({
      projectId: project.project_id,
      apiKey: project.api_key
    })
  }

  const getEmptyState = () => (
    <Card className="text-center py-12">
      <Card.Content>
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <Settings className="w-8 h-8 text-gray-400" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('tms.noProjectsConfigured')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {t('tms.addFirstProjectDescription')}
            </p>
            <Button onClick={() => setShowAddModal(true)} className="inline-flex items-center">
              <Plus className="w-4 h-4 mr-2" />
              {t('tms.addFirstProject')}
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  )

  const getErrorState = () => (
    <Card className="border-red-200 dark:border-red-800">
      <Card.Content className="p-6">
        <div className="flex items-center space-x-3">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <div>
            <h3 className="text-lg font-medium text-red-900 dark:text-red-100">
              {t('tms.loadingError')}
            </h3>
            <p className="text-red-600 dark:text-red-400 mt-1">
              {error?.response?.data?.detail || error?.message || t('tms.genericError')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="mt-3 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('common.retry')}
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  )

  const getLoadingState = () => (
    <Card>
      <Card.Content className="p-6">
        <div className="flex items-center justify-center space-x-3">
          <Loader className="w-6 h-6 animate-spin text-blue-500" />
          <span className="text-gray-600 dark:text-gray-400">
            {t('tms.loadingProjects')}
          </span>
        </div>
      </Card.Content>
    </Card>
  )

  if (isLoading) return getLoadingState()
  if (error) return getErrorState()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('tms.configuredProjects')} ({projects.length})
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            {t('tms.projectsDescription')}
          </p>
        </div>
        {projects.length > 0 && (
          <Button onClick={() => setShowAddModal(true)} className="flex items-center">
            <Plus className="w-4 h-4 mr-2" />
            {t('tms.addProject')}
          </Button>
        )}
      </div>

      {/* Projects List */}
      {projects.length === 0 ? (
        getEmptyState()
      ) : (
        <div className="grid gap-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.project_id}
              project={project}
              isDefault={project.project_id === defaultProjectId}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              onSetDefault={handleSetDefault}
              onTestConnection={handleTestConnection}
            />
          ))}
        </div>
      )}


      {/* Modals */}
      <AddProjectModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          // Projects will be refetched automatically due to query invalidation
        }}
      />

      <ProjectEditor
        project={editingProject}
        isOpen={!!editingProject}
        onClose={() => setEditingProject(null)}
        onSuccess={() => {
          setEditingProject(null)
        }}
        isDefault={editingProject?.project_id === defaultProjectId}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('tms.confirmDeleteProject')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('tms.deleteProjectWarning', { name: showDeleteConfirm.name })}
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={isDeletingProject}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={confirmDelete}
                  loading={isDeletingProject}
                  disabled={isDeletingProject}
                >
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TMSProjectManager